import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, TrendingDown, UserCheck, Rocket, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

/**
 * Predictions tab — forward-looking AI governance.
 *
 * Three feeds:
 *   • Stale forecast — assets predicted to go stale based on their first-week
 *     view trajectory.
 *   • Orphan owner suggestions — for each orphaned asset, propose 1-3
 *     candidate owners based on engagement + dataset usage signals.
 *   • Promotion candidates — "hidden gems" with high views concentrated in
 *     very few viewers; great candidates for auto-share.
 */
export default function PredictionsTab({ active }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reassignBusy, setReassignBusy] = useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPredictions(true);
      setData(res);
    } catch (e) {
      toast.error(e.message || 'Failed to load predictions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active && !data) load();
  }, [active, data, load]);

  const handleReassign = async (item, candidate) => {
    setReassignBusy(`${item.object_id}:${candidate.user_id}`);
    try {
      await api.reassignOwner({
        object_type: item.object_type,
        object_id: item.object_id,
        new_owner_id: candidate.user_id,
      });
      toast.success(`Reassigned to ${candidate.user_email}`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Reassign failed');
    } finally {
      setReassignBusy('');
    }
  };

  if (loading && !data) {
    return <div className="p-4 text-sm text-slate-500" data-testid="predictions-loading">Analyzing usage patterns…</div>;
  }
  if (!data) {
    return (
      <div className="p-6 text-center" data-testid="predictions-empty">
        <Sparkles className="mx-auto mb-2 text-slate-400" size={20} />
        <div className="text-sm text-slate-500">Predictions unavailable.</div>
      </div>
    );
  }

  const stale = data.stale_forecast || [];
  const orphans = data.orphan_owner_suggestions || [];
  const promos = data.promotion_candidates || [];

  const empty = stale.length === 0 && orphans.length === 0 && promos.length === 0;

  return (
    <div className="space-y-8" data-testid="governance-predictions">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Sparkles size={12} className="text-emerald-500" />
          Powered by AI. Predictions refresh on demand.
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-predictions-btn">
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {empty && (
        <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 text-sm">
          <Sparkles className="mx-auto mb-2 text-emerald-400" size={24} />
          <div className="font-medium">Your workspace looks healthy!</div>
          <div className="text-xs mt-1">No stale forecasts, orphan reassignments, or hidden gems to surface right now.</div>
        </div>
      )}

      {stale.length > 0 && (
        <section data-testid="predictions-stale-forecast">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="text-amber-500" size={16} />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Likely-stale assets</h3>
            <span className="text-xs text-slate-400">— {stale.length} predicted</span>
          </div>
          <div className="space-y-2">
            {stale.map((p, i) => (
              <div key={`${p.object_type}-${p.object_id}-${i}`} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${
                        p.risk === 'high' ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' :
                        p.risk === 'medium' ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' :
                        'bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800'
                      }`}>{p.risk.toUpperCase()} RISK</span>
                      <span className="text-xs text-slate-500 capitalize">{p.object_type}</span>
                    </div>
                    <div className="mt-1 font-semibold text-slate-800 dark:text-slate-200 truncate">{p.object_name}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{p.reason}</div>
                    {p.nudge && (
                      <div className="mt-2 pl-3 border-l-2 border-emerald-300 text-sm text-slate-700 dark:text-slate-300 italic">
                        <Sparkles size={12} className="inline mr-1 text-emerald-500" />{p.nudge}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-slate-400 shrink-0">
                    <div>{p.view_count} view{p.view_count === 1 ? '' : 's'}</div>
                    <div className="mt-0.5">{p.unique_viewers} viewer{p.unique_viewers === 1 ? '' : 's'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {orphans.length > 0 && (
        <section data-testid="predictions-orphans">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="text-sky-500" size={16} />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Suggested new owners</h3>
            <span className="text-xs text-slate-400">— {orphans.length} orphan{orphans.length === 1 ? '' : 's'}</span>
          </div>
          <div className="space-y-3">
            {orphans.map((o, i) => (
              <div key={`${o.object_type}-${o.object_id}-${i}`} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 capitalize">{o.object_type}</div>
                    <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">{o.object_name}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {(o.candidates || []).map(c => {
                    const busyKey = `${o.object_id}:${c.user_id}`;
                    return (
                      <div key={c.user_id} className="flex items-center justify-between gap-3 pl-3 py-2 border-l-2 border-emerald-200 dark:border-emerald-800">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{c.user_name || c.user_email}</div>
                          <div className="text-xs text-slate-500">
                            {c.signal === 'engagement' && `${c.score} recent interaction${c.score === 1 ? '' : 's'} with this asset`}
                            {c.signal === 'dataset_usage' && `Frequent user of the underlying dataset`}
                            {c.signal === 'most_active_admin' && `Most active admin in the workspace`}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={reassignBusy === busyKey}
                          onClick={() => handleReassign(o, c)}
                          data-testid={`reassign-${o.object_id}-${c.user_id}`}
                        >
                          {reassignBusy === busyKey ? 'Assigning…' : (<>Assign <ChevronRight size={12} className="ml-1" /></>)}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {promos.length > 0 && (
        <section data-testid="predictions-promotion">
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="text-emerald-500" size={16} />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Hidden gems worth sharing</h3>
            <span className="text-xs text-slate-400">— {promos.length}</span>
          </div>
          <div className="space-y-2">
            {promos.map((p, i) => (
              <div key={`${p.object_type}-${p.object_id}-${i}`} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500 capitalize">{p.object_type}</div>
                    <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">{p.object_name}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{p.reason}</div>
                    {p.nudge && (
                      <div className="mt-2 pl-3 border-l-2 border-emerald-300 text-sm text-slate-700 dark:text-slate-300 italic">
                        <Sparkles size={12} className="inline mr-1 text-emerald-500" />{p.nudge}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-slate-400 shrink-0">
                    <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{p.view_count}</div>
                    <div>views</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
